/*
 * i18next setup — Solvait-AIHR Console.
 *
 * Per DESIGN_SYSTEM.md §10:
 *   - Every UI string lives in the JSON catalogs (en.json / ar.json).
 *   - No hardcoded strings in components.
 *   - Switching languages updates html lang + dir without a reload.
 *   - User preference persists across sessions (localStorage).
 *
 * Library choice: i18next + react-i18next is the React-ecosystem
 * standard. Browser language detector picks "ar" automatically for
 * Saudi traffic on first visit; user toggle in the user menu
 * overrides + persists.
 */

import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import { initialLanguageFromUrl } from "../lib/locale-from-url";

import ar from "./ar.json";
import en from "./en.json";

export type Locale = "en" | "ar";

export const SUPPORTED_LOCALES: readonly Locale[] = ["en", "ar"] as const;

const urlLocale = initialLanguageFromUrl();

i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Apply ?lng= synchronously on first paint — do not rely on the
    // detector alone (async init + Clerk mount can flash English).
    ...(urlLocale ? { lng: urlLocale } : {}),
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    interpolation: {
      // React already escapes everything; double-escaping breaks
      // bilingual punctuation in Arabic.
      escapeValue: false,
    },
    detection: {
      // Priority: explicit URL ?lng= → localStorage → browser language
      // → fallback. localStorage key matches what users see in DevTools.
      order: ["querystring", "localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupQuerystring: "lng",
      lookupLocalStorage: "solvait_locale",
    },
    react: {
      // Without this Suspense flag react-i18next would hang in tests
      // until resources are loaded; we're loading synchronously so we
      // explicitly opt out.
      useSuspense: false,
    },
  });

/**
 * Side-effect: apply <html lang> and <html dir> from the current
 * i18next language. Called once on init and on every language
 * change so the document attributes stay in sync.
 */
function applyHtmlAttributes(lng: string): void {
  if (typeof document === "undefined") return;
  const locale: Locale = lng === "ar" ? "ar" : "en";
  document.documentElement.lang = locale;
  document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
}

applyHtmlAttributes(i18next.language);
i18next.on("languageChanged", applyHtmlAttributes);

/**
 * Convenience helper for components that don't need the React hook
 * (utilities, vanilla TS code). Equivalent to i18next.t but typed.
 */
export function t(key: string, options?: Record<string, unknown>): string {
  return i18next.t(key, options) as string;
}

/**
 * Programmatic locale switch. Persists via the detector cache.
 */
export function setLocale(locale: Locale): Promise<unknown> {
  return i18next.changeLanguage(locale);
}

/** Re-read `?lng=` and apply (auth pages after Clerk navigation). */
export function syncLocaleFromUrl(): Promise<unknown> {
  const fromUrl = initialLanguageFromUrl();
  if (!fromUrl || i18next.language === fromUrl) {
    return Promise.resolve(i18next.t);
  }
  return setLocale(fromUrl);
}

/** Read the current locale from i18next. */
export function currentLocale(): Locale {
  return (i18next.language === "ar" ? "ar" : "en") as Locale;
}

export default i18next;
